import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Stack from "@mui/material/Stack";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import MailOutline from "@mui/icons-material/MailOutline";
import PersonAddAltOutlined from "@mui/icons-material/PersonAddAltOutlined";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import ChevronRight from "@mui/icons-material/ChevronRight";

import { useWidget } from "/imports/state/WidgetContext";
import { useUrlFilters } from "/imports/libs/url";
import { GoogleSignInButton, useGoogleSignIn } from "/imports/ui/components/shared";

function SignupChooserView() {
  const navigate = useNavigate();
  const { slug } = useWidget();
  const { filters } = useUrlFilters({});
  const [error, setError] = useState(null);
  const { handleCode, pending } = useGoogleSignIn({ onError: setError });

  const query = filters.redirect ? `?redirect=${filters.redirect}` : "";
  const loginTo = `/${slug}/login${query}`;

  const iconSx = { fontSize: { xs: "1.625rem", sm: "2.1875rem" }, flexShrink: 0 };

  const options = [
    {
      icon: <PersonAddAltOutlined sx={iconSx} />,
      title: "I'm new here",
      description: "Create a brand-new account.",
      to: `/${slug}/register${query}`
    },
    {
      icon: <ConfirmationNumberOutlined sx={iconSx} />,
      title: "I have an access code",
      description: "Your club gave you a code to claim your profile.",
      to: `/${slug}/claim${query}`
    },
    {
      icon: <MailOutline sx={iconSx} />,
      title: "My club already has my details",
      description: "We'll email you a secure link to finish setting up.",
      to: `/${slug}/email-signup${query}`
    },
    {
      icon: <BadgeOutlined sx={iconSx} />,
      title: "Are you a trainer with us?",
      description: "Staff sign-in — enter the code your club gave you.",
      to: `/${slug}/trainer-claim${query}`
    }
  ];

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" py={{ xs: 2, sm: 4 }}>
      <Card sx={{ maxWidth: "28.75rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 }, "&:last-child": { pb: { xs: 2, sm: 4 } } }}>
          <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
            Set up your account
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
            Choose how you'd like to get started.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <GoogleSignInButton onCode={handleCode} text="continue_with" disabled={pending} />

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
          </Divider>

          <Stack spacing={{ xs: 1.25, sm: 2 }}>
            {options.map((option) => (
              <Card key={option.title} variant="outlined">
                <CardActionArea onClick={() => navigate(option.to)} sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box display="flex" alignItems="center" gap={{ xs: 1.25, sm: 2 }}>
                    <Box color="primary.main" display="flex" sx={{ flexShrink: 0 }}>
                      {option.icon}
                    </Box>
                    <Box flexGrow={1} minWidth={0}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {option.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.4, mt: 0.25 }}>
                        {option.description}
                      </Typography>
                    </Box>
                    <ChevronRight color="action" sx={{ flexShrink: 0, fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
                  </Box>
                </CardActionArea>
              </Card>
            ))}
          </Stack>

          <Typography variant="body2" textAlign="center" mt={3}>
            Already have an account?{" "}
            <Link component={RouterLink} to={loginTo}>
              Sign in
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SignupChooserView;
